import { Typography, Box, Chip } from "@mui/material"
import { Conditional, Stack } from "src/shared/components"
import SearchIcon from "@mui/icons-material/Search"
import { getIntegrationsListTableColumns } from "./getIntegrationsListColumns"
import { DataGridMui } from "src/shared/container/data-grid-mui"
import {
  FilterCondition,
  FilterOperator,
  LogicalOperator,
  SortDirection,
  useGetIntegrationsQuery
} from "src/graphql/quark/quark.type"
import { useMemo, useState } from "react"
import DebouncedInput from "src/shared/components/debounced-Input"
import ControlPointIcon from "@mui/icons-material/ControlPoint"
import { useSearchParams } from "react-router-dom"
import { useTypedSelector } from "src/shared/store/store"
import CreateUpdateIntegrationModal from "../../components/CreateUpdateIntegrationModal"
import { usePermission } from "src/shared/hooks/usePermission"

const IntegrationsList = () => {
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 20 })
  const [filters, setFilters] = useState<{ id: string; value: string }[]>([])
  const [sorting, setSorting] = useState<{ id?: null | string; desc?: null | boolean }>({
    id: "createdAt",
    desc: true
  })
  const [searchParams, setSearchParams] = useSearchParams()
  const currentWhLoc = useTypedSelector((state) => state.authentication.currentWhLoc)
  const { hasPermission } = usePermission()
  const filterConditions: FilterCondition[] = useMemo(() => {
    if (!filters?.length) return []
    return filters.map(
      (val) =>
        ({
          operator: val.id === "secondaryId" ? FilterOperator.Equal : FilterOperator.Like,
          field: val.id,
          value: val.value
        }) as FilterCondition
    )
  }, [filters])

  const { data, loading, refetch } = useGetIntegrationsQuery({
    fetchPolicy: "network-only",
    variables: {
      limit: pagination.pageSize,
      page: pagination.pageIndex + 1,
      locationId: currentWhLoc?.id,
      filter:
        filterConditions.length > 0
          ? [
              {
                conditions: filterConditions,
                logicalOperator: LogicalOperator.And
              }
            ]
          : undefined,
      sort: sorting?.id
        ? [
            {
              direction: sorting?.desc ? SortDirection.Desc : SortDirection.Asc,
              field: sorting.id as string
            }
          ]
        : []
    }
  })

  const handleCreateIntegration = () => {
    searchParams.set("mode", "create")
    setSearchParams(searchParams)
  }

  const handleEditIntegration = (integration: any) => {
    searchParams.set("mode", "edit")
    searchParams.set("id", integration.id)
    setSearchParams(searchParams)
  }

  const columns = getIntegrationsListTableColumns({ onEdit: handleEditIntegration, hasPermission })

  return (
    <Stack height={"100%"} p={2}>
      <Stack mb={3}>
        <Box>
          <Typography variant="h3">Integration Management</Typography>
          <Typography variant="body2" color="text.secondary">
            Manage third-party integrations and external services for your business.
          </Typography>
        </Box>
      </Stack>

      <DataGridMui
        id="integrations-list-table"
        columns={columns}
        data={data?.getIntegrations?.data ?? []}
        pageCount={data?.getIntegrations?.meta?.totalCount ?? 0}
        enableColumnFilters={false}
        enableSorting={false}
        skeletonCount={20}
        initialPageSize={20}
        rowsPerPageOptions={[20, 30, 50, 100]}
        isLoading={loading}
        onRefresh={refetch}
        paginationModel={pagination}
        paginationMode="server"
        sortingMode="server"
        filterMode="server"
        sortingModel={sorting as any}
        onSortModelChange={setSorting}
        onFilterChange={setFilters}
        onPaginationChange={setPagination}
        leftComponent={
          <DebouncedInput
            variant="rounded"
            value={(filters?.find((val) => val.id === "name")?.value ?? "") as string}
            onChange={(val) => {
              if (val === undefined) return

              setFilters((prev) => {
                const data = [...prev]
                const nameIndex = data?.findIndex((prev) => prev.id === "name")
                if (nameIndex === -1) {
                  data.push({ id: "name", value: val as string })
                  return data
                } else {
                  if (!val) return data.filter((prev) => prev.id !== "name")

                  data[nameIndex].value = val as string
                  return data
                }
              })
            }}
            startAdornment={<SearchIcon />}
            placeholder="Search by name"
            size="small"
          />
        }
        rightComponent={
          <Stack my={2} spacing={2} direction={"row"} alignItems={"center"}>
            <Conditional condition={hasPermission("system:integrations:create")}>
              <Chip
                size="small"
                onClick={handleCreateIntegration}
                color="primary"
                clickable
                variant="outlined"
                icon={<ControlPointIcon fontSize="small" />}
                label="Create"
              />
            </Conditional>
          </Stack>
        }
      />

      <Conditional
        condition={hasPermission("system:integrations:create") || hasPermission("system:integrations:update")}
      >
        <CreateUpdateIntegrationModal />
      </Conditional>
    </Stack>
  )
}

export default IntegrationsList
